import { useEffect, useState } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';

/**
 * Custom researchers (data connectors) — client side.
 *
 * Recipes live at World/{uid}/settings/dataConnectors/{id}; the API key never
 * reaches the browser (SecretIntegrations, server-only). Every change goes
 * through the dataConnector function, which validates the recipe and binds the
 * key to its host. Backend: functions/dataConnector.js.
 */

export const DATA_CONNECTORS_PATH = (userId) => `World/${userId}/settings/dataConnectors`;
export const DATA_CONNECTOR_LICENSE = 'hasDataConnectors';
export const DATA_CONNECTOR_ALERT_TYPE = 'data_connector_failed';
export const DATA_CONNECTOR_SETTINGS_TAB = 'dataSources';

// Twin of CONTACT_INPUTS in functions/helpers/dataConnectors.js.
export const CONTACT_INPUTS = {
  business_name: 'Company name',
  lead_website: 'Website',
  lead_domain: 'Domain (without www)',
  lead_city: 'City',
  lead_postcode: 'Postcode',
  lead_street: 'Street',
  lead_country: 'Country',
  lead_address: 'Full address',
  lead_email: 'Email',
  lead_telephone: 'Phone',
};

// Twin of COLUMN_TARGETS in functions/helpers/dataConnectors.js.
export const COLUMN_TARGETS = [
  { key: 'lead_description', label: 'Description' },
  { key: 'lead_industry', label: 'Industry' },
];

export const AUTH_TYPES = [
  { id: 'header', label: 'Header', hint: 'e.g. X-API-Key' },
  { id: 'bearer', label: 'Bearer token', hint: 'Authorization: Bearer …' },
  { id: 'query', label: 'URL parameter', hint: 'e.g. ?api_key=' },
  { id: 'basic', label: 'Basic auth', hint: 'user:password' },
  { id: 'none', label: 'No key', hint: 'Public API' },
];

export const REASON_LABELS = {
  auth_failed: 'API key rejected',
  bad_request: 'Request format rejected',
  provider_error: 'Platform server error',
  unreachable: 'Platform unreachable',
  blocked_host: 'Address blocked',
  response_too_large: 'Answer too large',
  bad_response: 'Answer is not JSON',
  key_missing: 'API key missing',
  connector_invalid: 'Setup incomplete',
  connector_missing: 'Data source deleted',
  rate_limited: 'Platform asked to slow down',
  missing_input: 'Contact data missing',
  no_match: 'Nothing found',
  ai_failed: 'Could not choose a value just now',
  contact_missing: 'Contact deleted',
  busy: 'Server busy',
};

export const reasonLabel = (reason) => REASON_LABELS[reason] || String(reason || 'Unknown error').replace(/_/g, ' ');

export const inputLabel = (key, customFields = []) => {
  if (String(key).startsWith('ai:')) return `${key.slice(3)} (AI-chosen value)`;
  if (CONTACT_INPUTS[key]) return CONTACT_INPUTS[key];
  const cf = customFields.find((f) => f.key === key);
  return cf ? cf.label : key;
};

export function mappingSourceLabel(source, fields = []) {
  if (source === 'count') return 'Number of results';
  if (source === 'summary') return 'All results as text';
  const [kind, path] = [source.slice(0, source.indexOf(':')), source.slice(source.indexOf(':') + 1)];
  const label = fields.find((f) => f.path === path)?.label || path;
  return kind === 'first' ? `${label} (first result)` : `${label} (all results)`;
}

/** Placeholders used anywhere in a recipe — what the platform receives. Twin of connectorInputs. */
export function recipeInputs(recipe) {
  const keys = new Set();
  const params = [...(recipe?.query || []), ...(recipe?.body || []), ...(recipe?.headers || [])];
  const texts = [recipe?.path, ...params.map((p) => p?.value)];
  for (const text of texts) {
    String(text || '').replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (_, k) => {
      keys.add(k.toLowerCase());
      return '';
    });
  }
  for (const p of params) {
    const name = String(p?.name || '').trim();
    if (p?.ai && name) keys.add(`ai:${name.toLowerCase()}`);
  }
  return [...keys];
}

/** One value per line. A single comma-separated line is split too. */
export function parseAiChoices(text) {
  const lines = String(text || '').split(/\n+/).map((s) => s.trim()).filter(Boolean);
  if (lines.length === 1 && lines[0].includes(',')) return lines[0].split(',').map((s) => s.trim()).filter(Boolean);
  return lines;
}

export async function callDataConnector(userId, action, payload = {}) {
  const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.dataConnector(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, action, worldId: userId }),
  });
  if (res.status === 429) return { ok: false, reason: 'busy', retryAfterSec: 20 };
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.error || `HTTP ${res.status}`);
    err.code = json.code || null;
    throw err;
  }
  return json;
}

export const runDataConnector = (userId, connectorId, contactId) =>
  callDataConnector(userId, 'run', { connectorId, contactId });

/** Live list of the account's connectors, newest first. */
export function useDataConnectors(userId) {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!userId) {
      setConnectors([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    return onValue(ref(database, DATA_CONNECTORS_PATH(userId)), (snap) => {
      const list = Object.entries(snap.val() || {})
        .map(([id, c]) => ({ ...c, id }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setConnectors(list);
      setLoading(false);
    }, () => setLoading(false));
  }, [userId]);
  return { connectors, loading };
}

export const CONNECTOR_IDENTITY_PATH = (userId) => `World/${userId}/settings/dataConnectorIdentity`;

/**
 * Who data source requests are signed as. Own setting first, else the account's
 * company name and login email — the same fallback the server uses.
 */
export function useConnectorIdentity(userId) {
  const [own, setOwn] = useState({});
  const [account, setAccount] = useState({});
  useEffect(() => {
    if (!userId) return undefined;
    const offOwn = onValue(ref(database, CONNECTOR_IDENTITY_PATH(userId)), (snap) => setOwn(snap.val() || {}), () => {});
    const offCompany = onValue(ref(database, `Users/${userId}/companyName`), (snap) => setAccount((a) => ({ ...a, company: snap.val() || '' })), () => {});
    const offEmail = onValue(ref(database, `Users/${userId}/email`), (snap) => setAccount((a) => ({ ...a, email: snap.val() || '' })), () => {});
    return () => { offOwn(); offCompany(); offEmail(); };
  }, [userId]);
  const identity = { company: own.company || account.company || '', email: own.email || account.email || '' };
  const save = (next) => set(ref(database, CONNECTOR_IDENTITY_PATH(userId)), {
    company: String(next.company || '').trim().slice(0, 80),
    email: String(next.email || '').trim().slice(0, 120),
    updatedAt: Date.now(),
  });
  return { own, account, identity, save };
}

/** Pause between two contacts of one connector in a bulk run. */
export const connectorGapMs = (rand = Math.random()) => 600 + Math.round(rand * 900);